import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box, Typography, Button, Accordion, AccordionSummary, AccordionDetails,
  CircularProgress, Divider, Tooltip,
} from '@mui/material'
import { ChevronDown, FilePlus, RefreshCw, Trash2, CheckCircle, XCircle, Eye, Play } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  fetchExtractedDocuments, updateDocValidation, approveAll, startExecution,
  previewAllConsolidations, openConsolidationPreview, previewSingleConsolidation,
} from '../../../../redux/actions/boConsolidationAction'
import BODocumentRow from './BODocumentRow'
import BODocumentPreview from './BODocumentPreview'
import BOConsolidationPreview from './BOConsolidationPreview'

const SECTIONS = [
  { key: 'new', icon: FilePlus, labelKey: 'du_bo_new_documents', label: 'New Documents' },
  { key: 'update', icon: RefreshCw, labelKey: 'du_bo_consolidations', label: 'Consolidations' },
  { key: 'abroge', icon: Trash2, labelKey: 'du_bo_abrogations', label: 'Abrogations' },
]

export default function BOReviewPanel() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { selectedBo, extractedDocs } = useSelector((s) => s.boConsolidation)
  const { loading } = extractedDocs

  const [previewDocId, setPreviewDocId] = useState(null)

  useEffect(() => {
    if (selectedBo?.id) {
      dispatch(fetchExtractedDocuments(selectedBo.id))
    }
  }, [dispatch, selectedBo])

  const handleApprove = (docId) => {
    dispatch(updateDocValidation(docId, 'approved'))
  }

  const handleReject = (docId) => {
    dispatch(updateDocValidation(docId, 'rejected'))
  }

  const handleApproveAll = (sectionKey) => {
    if (selectedBo?.id) {
      dispatch(approveAll(selectedBo.id, sectionKey))
    }
  }

  const handleRejectAll = (sectionKey) => {
    const docs = extractedDocs[sectionKey] || []
    docs.forEach((doc) => {
      if (doc.validation_status !== 'rejected') {
        dispatch(updateDocValidation(doc.id, 'rejected'))
      }
    })
  }

  const handleExecute = () => {
    if (selectedBo?.id) {
      dispatch(startExecution(selectedBo.id))
    }
  }

  // Route preview action: 'update' docs go to consolidation preview (3-pane viewer),
  // 'new' / 'abroge' go to the standard metadata/HTML editor.
  const handleRowPreview = (doc) => {
    if (doc.next_step === 'update') {
      // If no cached preview yet, kick off compute first
      if (['approved', 'preview_failed'].includes(doc.validation_status)) {
        dispatch(previewSingleConsolidation(doc.id))
      }
      dispatch(openConsolidationPreview(doc.id))
    } else {
      setPreviewDocId(doc.id)
    }
  }

  // Count approved docs
  const approvedCount = ['new', 'update', 'abroge'].reduce((sum, key) => {
    return sum + (extractedDocs[key] || []).filter((d) => d.validation_status === 'approved').length
  }, 0)

  const totalCount = ['new', 'update', 'abroge'].reduce((sum, key) => {
    return sum + (extractedDocs[key] || []).length
  }, 0)

  // Consolidation preview gating: count approved updates that need preview/confirmation
  const updates = extractedDocs.update || []
  const updatesNeedingPreview = updates.filter((d) =>
    ['approved', 'previewed', 'preview_failed'].includes(d.validation_status)
  )
  const hasUpdatesPendingReview = updatesNeedingPreview.length > 0
  const updatesAwaitingPreview = updates.filter((d) => d.validation_status === 'approved').length

  const handleGenerateAllPreviews = () => {
    if (selectedBo?.id) {
      dispatch(previewAllConsolidations(selectedBo.id))
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#c9a96e' }} />
      </Box>
    )
  }

  return (
    <Box>
      {/* Sections */}
      {SECTIONS.map(({ key, icon: Icon, labelKey, label }) => {
        const docs = extractedDocs[key] || []
        if (docs.length === 0) return null

        return (
          <Accordion
            key={key}
            defaultExpanded={docs.length > 0}
            sx={{
              mb: 2,
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
          >
            <AccordionSummary
              expandIcon={<ChevronDown size={18} />}
              sx={{ '&:hover': { backgroundColor: 'rgba(201,169,110,0.03)' } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                <Icon size={18} color="#c9a96e" />
                <Typography sx={{ fontWeight: 500 }}>
                  {t(labelKey, label)} ({docs.length})
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2, pb: 2 }}>
              {/* Bulk actions */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  size="small"
                  startIcon={<CheckCircle size={14} />}
                  onClick={() => handleApproveAll(key)}
                  sx={{ textTransform: 'none', color: '#4caf50' }}
                >
                  {t('du_bo_approve_all', 'Approve All')}
                </Button>
                <Button
                  size="small"
                  startIcon={<XCircle size={14} />}
                  onClick={() => handleRejectAll(key)}
                  sx={{ textTransform: 'none', color: '#9e9e9e' }}
                >
                  {t('du_bo_reject_all', 'Reject All')}
                </Button>
              </Box>

              <Divider sx={{ mb: 1 }} />

              {/* Document rows */}
              {docs.map((doc) => (
                <BODocumentRow
                  key={doc.id}
                  doc={doc}
                  onApprove={() => handleApprove(doc.id)}
                  onReject={() => handleReject(doc.id)}
                  onPreview={() => handleRowPreview(doc)}
                />
              ))}
            </AccordionDetails>
          </Accordion>
        )
      })}

      {totalCount === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {t('du_bo_no_documents', 'No documents extracted from this BO.')}
        </Typography>
      )}

      {/* Bottom bar */}
      {totalCount > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            {approvedCount}/{totalCount} {t('du_bo_approved', 'approved')}
          </Typography>
          {updatesAwaitingPreview > 0 && (
            <Tooltip
              title={t(
                'du_bo_generate_all_tooltip',
                'Compute consolidation previews for all approved amendment docs in this BO'
              )}
              arrow
            >
              <Button
                variant="outlined"
                startIcon={<Eye size={14} />}
                onClick={handleGenerateAllPreviews}
                sx={{
                  textTransform: 'none',
                  borderColor: '#c9a96e',
                  color: '#c9a96e',
                  '&:hover': { borderColor: '#b8944f', backgroundColor: 'rgba(201,169,110,0.04)' },
                }}
              >
                {t('du_bo_generate_all_previews', 'Generate all previews')} ({updatesAwaitingPreview})
              </Button>
            </Tooltip>
          )}
          <Tooltip
            title={
              hasUpdatesPendingReview
                ? t('du_bo_confirm_first_tooltip', 'Confirm all consolidation previews before executing')
                : ''
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                startIcon={<Play size={14} />}
                disabled={approvedCount === 0 || hasUpdatesPendingReview}
                onClick={handleExecute}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#c9a96e',
                  '&:hover': { backgroundColor: '#b8944f' },
                  '&.Mui-disabled': { backgroundColor: '#e0e0e0' },
                }}
              >
                {t('du_bo_execute_approved', 'Execute Approved')}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* Preview dialog (for new/abroge docs — metadata + HTML editor) */}
      <BODocumentPreview
        open={previewDocId !== null}
        onClose={() => setPreviewDocId(null)}
        docId={previewDocId}
      />

      {/* Consolidation preview viewer (for update docs — 3-pane diff + editor) */}
      <BOConsolidationPreview />
    </Box>
  )
}
